import { useState } from "react";

import { useGetUserExperienceFlagsQuery, useUpdateUserExperienceFlagsMutation, useSyncPartnerDocumentMutation } from "EmployerApplication/api";
import isIntuit from "Intuit/isIntuit";
import { useSelector } from "react-redux";
import companyToken from "Utils/companyToken";

import { inABTest } from "Common/Selectors";

import { DONT_HAVE_FILE_FILENAME } from "./constants";
import { convertFileToFormData } from "./utils";

export const useMainDocumentState = ({ fileInputName, originalFilename, onDocumentUpload }) => {
  const [uploading, setUploading] = useState(false);
  const [filename, setFilename] = useState(originalFilename);
  const [error, setError] = useState(null);
  const [uploadedInvalidDocument, setUploadedInvalidDocument] = useState(false);

  const [syncPartnerDocument, { isLoading: syncingDocument }] = useSyncPartnerDocumentMutation();

  const uploadFile = async (file) => {
    setUploading(true);
    setFilename(file.name);
    const formData = convertFileToFormData(file, fileInputName, false);
    const response = await onDocumentUpload(formData);
    const responseClone = response.clone();
    const data = await responseClone.json();
    setError(data?.errors?.[0]?.detail || null);
    setUploading(false);
    return response;
  };

  const explanationToFile = (explanation) => {
    let supportHeader;
    if (isIntuit()) {
      supportHeader = `This is an Intuit group. They did not have the document for ${fileInputName} and submitted the following explanation: ${explanation}`;
    } else {
      supportHeader = `This group did not have the document for ${fileInputName} and submitted the following explanation: ${explanation}`;
    }
    const file = new File([supportHeader], DONT_HAVE_FILE_FILENAME, {
      type: "text/plain",
      lastModified: new Date(),
    });

    return file;
  };

  return {
    error,
    filename,
    fullfilled: !!filename && !error,
    uploading,
    uploadedInvalidDocument,
    setUploading,
    setFilename,
    setError,
    setUploadedInvalidDocument,
    syncPartnerDocument,
    syncingDocument,
    uploadFile,
    explanationToFile,
  };
};

export const useUserFlagsState = ({ fileInputName }) => {
  const { isLoading, data: userExperienceFlags } = useGetUserExperienceFlagsQuery({ companyIDToken: companyToken() });
  const [updateUserExperienceFlags] = useUpdateUserExperienceFlagsMutation();

  const persistedAlternativeType = userExperienceFlags?.alternative_document_data?.[fileInputName]?.answer || null;
  const persistedExplanation = userExperienceFlags?.alternative_document_data?.[fileInputName]?.explanation || null;

  const updateFlags = (flags) =>
    updateUserExperienceFlags({
      companyIDToken: companyToken(),
      alternative_document_data: {
        ...userExperienceFlags.alternative_document_data,
        [fileInputName]: {
          ...(userExperienceFlags.alternative_document_data?.[fileInputName] || {}),
          ...flags,
        },
      },
    });

  return {
    isLoading,
    updateFlags,
    persistedAlternativeType,
    persistedExplanation,
  };
};

export const useAlternativeDocumentsState = ({
  matchedPrimaryDocuments,
  matchedSecondaryDocuments,
  primaryAlternatives,
  secondaryAlternatives,
  userFlagsState,
}) => {
  const [uploading, setUploading] = useState(false);
  const [altFilenames, setAltFilenames] = useState({
    primary: (primaryAlternatives || []).reduce((acc, _doc, i) => {
      acc[i] = matchedPrimaryDocuments?.[i];
      return acc;
    }, {}),
    secondary: (secondaryAlternatives || []).reduce((acc, _doc, i) => {
      acc[i] = matchedSecondaryDocuments?.[i];
      return acc;
    }, {}),
  });

  const [altErrors, setAltErrors] = useState({ primary: {}, secondary: {} });
  const [altDocsCardIsOpen, setAltDocsCardIsOpen] = useState(false);

  const inDocumentAlternativesABTest = useSelector((state) => inABTest(state, "document_alternatives"));

  const showDocumentAlternatives = primaryAlternatives && secondaryAlternatives;

  const { persistedAlternativeType, persistedExplanation } = userFlagsState;

  const altFullfilled = ["primary", "secondary"].reduce((acc, type) => {
    const filenames = Object.values(altFilenames[type]).filter(Boolean);
    const errors = Object.values(altErrors[type]).filter(Boolean);
    const altenativesCount = type === "primary" ? primaryAlternatives?.length : secondaryAlternatives?.length;
    acc[type] = altenativesCount === filenames.length && errors.length === 0;
    return acc;
  }, {});

  altFullfilled.NoDocuments = persistedExplanation?.length > 0;

  const fullfilled = altFullfilled[persistedAlternativeType];
  return {
    altDocsCardIsOpen,
    altErrors,
    altFilenames,
    fullfilled,
    inDocumentAlternativesABTest,
    setAltFilenames,
    setAltErrors,
    setAltDocsCardIsOpen,
    setUploading,
    showDocumentAlternatives,
    uploading,
  };
};
